import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  MenuItem,
  Tabs,
  Tab,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  Select,
  TextField,
  Typography,
  InputAdornment,
  IconButton
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PasswordRequirements from './PasswordRequirements';

import StatsCharts from './StatsCharts';
import { tokens } from './theme';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const LOG_TABS = [
  { key: 'personel', label: 'Personel Logları' },
  { key: 'ogrenci', label: 'Öğrenci Logları' },
  { key: 'authority', label: 'Yetkili Logları' }
];

const emptySearch = {
  query: '',
  username: '',
  tc_no: '',
  name: '',
  surname: '',
  user_type: ''
};

function AuthorityPortal() {
  const [token, setToken] = useState(localStorage.getItem('authorityToken') || '');
  const [profile, setProfile] = useState(() => {
    const raw = localStorage.getItem('authorityProfile');
    return raw ? JSON.parse(raw) : null;
  });
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginMessage, setLoginMessage] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const [searchCriteria, setSearchCriteria] = useState(emptySearch);
  const [searchResults, setSearchResults] = useState([]);
  const [searchMessage, setSearchMessage] = useState('');
  const [isSearching, setIsSearching] = useState(false);

  const [selectedUser, setSelectedUser] = useState(null);
  const [updateForm, setUpdateForm] = useState({ name: '', surname: '', phone: '', email: '', tc_no: '' });
  const [updateMessage, setUpdateMessage] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  const [passwordForm, setPasswordForm] = useState({ new_password: '', target: 'both' });
  const [passwordMessage, setPasswordMessage] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  // Hata panosu: kullanıcıların hangi adımda takıldığını görmek için.
  const [stats, setStats] = useState(null);
  const [statsDays, setStatsDays] = useState(30);
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  const [activeLogTab, setActiveLogTab] = useState(LOG_TABS[0].key);
  const [logFilters, setLogFilters] = useState({ query: '', ip: '' });
  const [appliedLogFilters, setAppliedLogFilters] = useState({ query: '', ip: '' });
  const [logEntries, setLogEntries] = useState([]);
  const [logMessage, setLogMessage] = useState('');
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);

  const isAuthenticated = useMemo(() => Boolean(token), [token]);

  useEffect(() => {
    if (!token) {
      localStorage.removeItem('authorityToken');
      localStorage.removeItem('authorityProfile');
    }
  }, [token]);

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoginMessage('');
    setIsLoggingIn(true);
    try {
      const response = await fetch(`${API_BASE_URL}/authority/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginForm)
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Giriş işlemi başarısız oldu.');
      }
      localStorage.setItem('authorityToken', data.access_token);
      localStorage.setItem('authorityProfile', JSON.stringify(data.profile));
      setToken(data.access_token);
      setProfile(data.profile);
      setLoginForm({ username: '', password: '' });
    } catch (error) {
      setLoginMessage(error.message);
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    setToken('');
    setProfile(null);
    setSelectedUser(null);
    setSearchResults([]);
    setSearchCriteria(emptySearch);
    setActiveLogTab(LOG_TABS[0].key);
    setLogEntries([]);
    const defaultLogFilters = { query: '', ip: '' };
    setLogFilters(defaultLogFilters);
    setAppliedLogFilters(defaultLogFilters);
    setLogMessage('');
  };

  const handleSearch = async (event) => {
    event.preventDefault();
    setSearchMessage('');
    if (!token) return;
    const payload = Object.fromEntries(
      Object.entries(searchCriteria).filter(([_, value]) => value)
    );
    if (Object.keys(payload).length === 0) {
      setSearchMessage('En az bir kriter girmelisiniz.');
      return;
    }
    setIsSearching(true);
    try {
      const response = await fetch(`${API_BASE_URL}/authority/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      if (checkAuthAndHandleError(response)) return;
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Arama sırasında hata oluştu.');
      }
      const normalized = (data.results || []).map((item) => ({
        ...item,
        is_locked: Boolean(item.is_locked),
        is_disabled: Boolean(item.is_disabled)
      }));
      setSearchResults(normalized);
      if ((data.results || []).length === 0) {
        setSearchMessage('Eşleşen kayıt bulunamadı.');
      }
    } catch (error) {
      setSearchMessage(error.message);
    } finally {
      setIsSearching(false);
    }
  };

  const checkAuthAndHandleError = useCallback((response) => {
    if (response.status === 401) {
      // Session süresi bitmiş, otomatik logout
      setToken('');
      setProfile(null);
      setSelectedUser(null);
      setSearchResults([]);
      setSearchCriteria(emptySearch);
      setActiveLogTab(LOG_TABS[0].key);
      setLogEntries([]);
      const defaultLogFilters = { query: '', ip: '' };
      setLogFilters(defaultLogFilters);
      setAppliedLogFilters(defaultLogFilters);
      setLogMessage('');
      setLoginMessage('Oturum süreniz dolmuş. Lütfen tekrar giriş yapınız.');
      return true;
    }
    return false;
  }, []);

  const fetchUserDetails = async (username, userType) => {
    if (!token) return;
    try {
      const query = userType ? `?user_type=${userType}` : '';
      const response = await fetch(`${API_BASE_URL}/authority/user/${username}${query}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (checkAuthAndHandleError(response)) return;
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Kullanıcı bilgileri getirilemedi.');
      }
      const user = {
        ...data.user,
        is_locked: Boolean(data.user?.is_locked),
        is_disabled: Boolean(data.user?.is_disabled)
      };
      setSelectedUser(user);
      setUpdateForm({
        name: user.name || '',
        surname: user.surname || '',
        phone: user.phone || '',
        email: user.email || '',
        tc_no: user.tc_no || ''
      });
      setPasswordForm({ new_password: '', target: 'both' });
    } catch (error) {
      setUpdateMessage(error.message);
    }
  };

  const handleSelectUser = (user) => {
    setUpdateMessage('');
    setPasswordMessage('');
    fetchUserDetails(user.username, user.user_type);
  };

  const handleUpdateUser = async () => {
    if (!token || !selectedUser) return;
    setUpdateMessage('');
    setIsUpdating(true);
    try {
      const payload = {
        user_type: selectedUser.user_type,
        ...updateForm
      };
      if (payload.tc_no) {
        payload.tc_no = payload.tc_no.replace(/\s+/g, '');
      }
      const response = await fetch(`${API_BASE_URL}/authority/user/${selectedUser.username}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      if (checkAuthAndHandleError(response)) return;
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Kullanıcı güncellenemedi.');
      }
      setUpdateMessage('Kullanıcı bilgileri güncellendi.');
    } catch (error) {
      setUpdateMessage(error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePasswordReset = async () => {
    if (!token || !selectedUser) return;
    setPasswordMessage('');
    if (!passwordForm.new_password) {
      setPasswordMessage('Yeni şifreyi giriniz.');
      return;
    }
    setIsResettingPassword(true);
    try {
      const payload = {
        username: selectedUser.username,
        user_type: selectedUser.user_type,
        target: passwordForm.target,
        new_password: passwordForm.new_password
      };
      const response = await fetch(`${API_BASE_URL}/authority/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      if (checkAuthAndHandleError(response)) return;
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Şifre güncellenemedi.');
      }
      setPasswordMessage('Şifre başarıyla güncellendi.');
      setPasswordForm((prev) => ({ ...prev, new_password: '' }));
    } catch (error) {
      setPasswordMessage(error.message);
    } finally {
      setIsResettingPassword(false);
    }
  };

  const fetchStats = useCallback(async () => {
    if (!token) return;
    setIsLoadingStats(true);
    try {
      const response = await fetch(`${API_BASE_URL}/authority/stats?days=${statsDays}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (checkAuthAndHandleError(response)) return;
      const data = await response.json();
      if (response.ok) setStats(data);
    } catch (error) {
      setStats(null);
    } finally {
      setIsLoadingStats(false);
    }
  }, [token, statsDays, checkAuthAndHandleError]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const fetchLogs = useCallback(async () => {
    if (!token) return;
    setIsLoadingLogs(true);
    setLogMessage('');
    try {
      const params = new URLSearchParams();
      params.append('actor_type', activeLogTab);
      params.append('limit', '100');
      if (appliedLogFilters.query) {
        params.append('search', appliedLogFilters.query);
      }
      if (appliedLogFilters.ip) {
        params.append('ip', appliedLogFilters.ip);
      }
      const response = await fetch(`${API_BASE_URL}/authority/logs?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (checkAuthAndHandleError(response)) return;
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Loglar getirilemedi.');
      }
      setLogEntries(data.logs || []);
      if ((data.logs || []).length === 0) {
        setLogMessage('Kayıt bulunamadı.');
      }
    } catch (error) {
      setLogMessage(error.message);
    } finally {
      setIsLoadingLogs(false);
    }
  }, [token, activeLogTab, appliedLogFilters, checkAuthAndHandleError]);

  useEffect(() => {
    if (token) {
      fetchLogs();
    }
  }, [token, fetchLogs]);

  const handleLogTabChange = (_, value) => {
    setActiveLogTab(value);
  };

  const handleLogSearch = () => {
    setAppliedLogFilters(logFilters);
  };

  const handleLogReset = () => {
    const defaults = { query: '', ip: '' };
    setLogFilters(defaults);
    setAppliedLogFilters(defaults);
  };

  if (!isAuthenticated) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" color="primary" gutterBottom>
            Yetkili Girişi
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Yetkili hesabınız ile giriş yapınız.
          </Typography>
        </Box>
        <Card>
          <CardContent>
            <form onSubmit={handleLogin}>
              <TextField
                label="Kullanıcı Adı"
                fullWidth
                sx={{ mb: 2 }}
                value={loginForm.username}
                onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                required
              />
              <TextField
                label="Şifre"
                type="password"
                fullWidth
                sx={{ mb: 2 }}
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                required
              />
              {loginMessage && (
                <Typography color="error" sx={{ mb: 2 }}>
                  {loginMessage}
                </Typography>
              )}
              <Button type="submit" variant="contained" fullWidth disabled={isLoggingIn}>
                {isLoggingIn ? 'Giriş yapılıyor...' : 'Giriş Yap'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <div>
          <Typography variant="h5" color="primary">
            Yetkili Paneli
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {profile?.name} {profile?.surname} ({profile?.email})
          </Typography>
        </div>
        <Button variant="outlined" color="error" onClick={handleLogout}>
          Çıkış
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={5}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Kullanıcı Arama
              </Typography>
              <form onSubmit={handleSearch}>
                <TextField
                  label="Genel Arama"
                  fullWidth
                  sx={{ mb: 2 }}
                  value={searchCriteria.query}
                  onChange={(e) => setSearchCriteria({ ...searchCriteria, query: e.target.value })}
                />
                <TextField
                  label="Kullanıcı Adı"
                  fullWidth
                  sx={{ mb: 2 }}
                  value={searchCriteria.username}
                  onChange={(e) => setSearchCriteria({ ...searchCriteria, username: e.target.value })}
                />
                <TextField
                  label="TC Kimlik No"
                  fullWidth
                  sx={{ mb: 2 }}
                  value={searchCriteria.tc_no}
                  onChange={(e) => setSearchCriteria({ ...searchCriteria, tc_no: e.target.value })}
                />
                <TextField
                  label="Ad"
                  fullWidth
                  sx={{ mb: 2 }}
                  value={searchCriteria.name}
                  onChange={(e) => setSearchCriteria({ ...searchCriteria, name: e.target.value })}
                />
                <TextField
                  label="Soyad"
                  fullWidth
                  sx={{ mb: 2 }}
                  value={searchCriteria.surname}
                  onChange={(e) => setSearchCriteria({ ...searchCriteria, surname: e.target.value })}
                />
                <Select
                  value={searchCriteria.user_type}
                  fullWidth
                  displayEmpty
                  sx={{ mb: 2 }}
                  onChange={(e) => setSearchCriteria({ ...searchCriteria, user_type: e.target.value })}
                >
                  <MenuItem value="">Tümü</MenuItem>
                  <MenuItem value="ogrenci">Öğrenci</MenuItem>
                  <MenuItem value="personel">Personel</MenuItem>
                </Select>
                {searchMessage && (
                  <Typography color="error" sx={{ mb: 2 }}>
                    {searchMessage}
                  </Typography>
                )}
                <Button type="submit" variant="contained" fullWidth disabled={isSearching}>
                  {isSearching ? 'Aranıyor...' : 'Ara'}
                </Button>
              </form>
            </CardContent>
          </Card>
          <Card sx={{ mt: 3, maxHeight: 360, overflowY: 'auto' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sonuçlar
              </Typography>
              {searchResults.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Henüz sonuç yok.
                </Typography>
              )}
              {searchResults.map((user) => (
                <Box
                  key={`${user.user_type}-${user.username}`}
                  sx={{
                    border: '1px solid',
                    borderColor: selectedUser?.username === user.username ? 'primary.main' : 'grey.300',
                    borderRadius: 2,
                    p: 1.5,
                    mb: 1.5,
                    cursor: 'pointer'
                  }}
                  onClick={() => handleSelectUser(user)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {user.name} {user.surname}
                    </Typography>
                    {user.is_locked && (
                      <LockIcon fontSize="small" sx={{ color: 'error.main' }} />
                    )}
                    {user.is_disabled && (
                      <Typography sx={{ color: '#BD4C35', fontWeight: 700, textDecoration:'underline' }}>
                        HESAP DEVRE DIŞI
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {user.username} • {user.user_type?.toUpperCase()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user.email} • {user.phone}
                  </Typography>
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Kullanıcı Bilgileri
              </Typography>
              {selectedUser ? (
                <>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Ad"
                        fullWidth
                        value={updateForm.name}
                        onChange={(e) => setUpdateForm({ ...updateForm, name: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Soyad"
                        fullWidth
                        value={updateForm.surname}
                        onChange={(e) => setUpdateForm({ ...updateForm, surname: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="P.O Box / TC"
                        fullWidth
                        value={updateForm.tc_no}
                        onChange={(e) => setUpdateForm({ ...updateForm, tc_no: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Telefon"
                        fullWidth
                        value={updateForm.phone}
                        onChange={(e) => setUpdateForm({ ...updateForm, phone: e.target.value })}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="E-posta"
                        fullWidth
                        value={updateForm.email}
                        onChange={(e) => setUpdateForm({ ...updateForm, email: e.target.value })}
                      />
                    </Grid>
                  </Grid>
                  {(selectedUser.is_locked || selectedUser.is_disabled) && (
                    <Box sx={{ mt: 2 }}>
                      {selectedUser.is_locked && (
                        <Typography sx={{ color: 'error.main', display: 'flex', alignItems: 'center', mb: 0.5 }}>
                          <LockIcon fontSize="small" sx={{ mr: 0.5 }} /> Hesap kilitli
                        </Typography>
                      )}
                      {selectedUser.is_disabled && (
                        <Typography sx={{ color: '#BD4C35', fontWeight: 700, textDecoration:'underline'}}>
                          HESAP DEVRE DIŞI
                        </Typography>
                      )}
                    </Box>
                  )}
                  {updateMessage && (
                    <Typography sx={{ color: updateMessage.includes('başarıyla') ? 'green' : 'error.main', mt: 2 }}>
                      {updateMessage}
                    </Typography>
                  )}
                  <Button
                    variant="contained"
                    sx={{ mt: 2 }}
                    onClick={handleUpdateUser}
                    disabled={isUpdating}
                  >
                    {isUpdating ? 'Güncelleniyor...' : 'Bilgileri Güncelle'}
                  </Button>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Düzenlenecek kullanıcı seçiniz.
                </Typography>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Şifre Güncelle
              </Typography>
              {selectedUser ? (
                <>
                  <TextField
                    label="Yeni Şifre"
                    type={showNewPassword ? 'text' : 'password'}
                    fullWidth
                    sx={{ mb: 1 }}
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowNewPassword((v) => !v)} edge="end">
                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                  {/* Yetkili panelinde yalnızca AD ve Google'ın gerçekten uyguladığı
                      kurallar geçerlidir; TC kontrolü burada uygulanmaz (showTc=false),
                      böylece "Lhu<TC>*" gibi geçici şifreler atanabilir. */}
                  <Typography variant="caption" sx={{
                    display: 'block', mb: 1.5, color: tokens.inkMuted, lineHeight: 1.5
                  }}>
                    Burada yalnızca Active Directory ve Google'ın kendi kuralları geçerlidir.
                    TC kimlik numarası kısıtı uygulanmaz — <code>Lhu12345678901*</code> gibi
                    geçici şifreler atayabilirsiniz.
                  </Typography>
                  <PasswordRequirements
                    password={passwordForm.new_password}
                    username={selectedUser.username}
                    name={selectedUser.name}
                    surname={selectedUser.surname}
                    showTc={false}
                  />
                  <Typography variant="subtitle2" gutterBottom>
                    Hedef
                  </Typography>
                  <RadioGroup
                    row
                    value={passwordForm.target}
                    onChange={(e) => setPasswordForm({ ...passwordForm, target: e.target.value })}
                  >
                    <FormControlLabel value="ad" control={<Radio />} label="Active Directory" />
                    <FormControlLabel value="google" control={<Radio />} label="Google" />
                    <FormControlLabel value="both" control={<Radio />} label="Her ikisi" />
                  </RadioGroup>
                  {passwordMessage && (
                    <Typography sx={{ color: passwordMessage.includes('başarıyla') ? 'green' : 'error.main', mt: 2 }}>
                      {passwordMessage}
                    </Typography>
                  )}
                  <Divider sx={{ my: 2 }} />
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handlePasswordReset}
                    disabled={isResettingPassword}
                  >
                    {isResettingPassword ? 'Güncelleniyor...' : 'Şifreyi Güncelle'}
                  </Button>
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Şifre değiştirmek için kullanıcı seçiniz.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Hata panosu: şifre değiştirme akışının neresinde takılıyorlar? */}
      <Card sx={{ mt: 4 }}>
        <CardContent sx={{ p: 3 }}>
          <StatsCharts
            stats={stats}
            days={statsDays}
            onDaysChange={setStatsDays}
            loading={isLoadingStats}
          />
        </CardContent>
      </Card>

      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Loglar
          </Typography>
          <Tabs
            value={activeLogTab}
            onChange={handleLogTabChange}
            variant="scrollable"
            allowScrollButtonsMobile
            sx={{ mb: 2 }}
          >
            {LOG_TABS.map((tab) => (
              <Tab key={tab.key} value={tab.key} label={tab.label} />
            ))}
          </Tabs>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Kullanıcı / Öğrenci No"
                fullWidth
                value={logFilters.query}
                onChange={(e) => setLogFilters({ ...logFilters, query: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="IP Adresi"
                fullWidth
                value={logFilters.ip}
                onChange={(e) => setLogFilters({ ...logFilters, ip: e.target.value })}
              />
            </Grid>
            <Grid
              item
              xs={12}
              sm={4}
              sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}
            >
              <Button variant="contained" onClick={handleLogSearch}>
                Ara
              </Button>
              <Button variant="outlined" onClick={handleLogReset}>
                Temizle
              </Button>
            </Grid>
          </Grid>
          {isLoadingLogs && (
            <Typography variant="body2" color="text.secondary">
              Loglar yükleniyor...
            </Typography>
          )}
          {!isLoadingLogs && logMessage && (
            <Typography variant="body2" color={logMessage === 'Kayıt bulunamadı.' ? 'text.secondary' : 'error'}>
              {logMessage}
            </Typography>
          )}
          <Box sx={{ mt: 2, maxHeight: 360, overflowY: 'auto' }}>
            {logEntries.map((entry) => {
              const success = entry.status === 'success';
              const statusLabel = success ? 'Başarılı' : 'Başarısız';
              const statusColor = success ? 'success' : 'error';
              return (
                <Box
                  key={entry.id}
                  sx={{
                    border: '1px solid',
                    borderColor: success ? 'success.light' : 'error.light',
                    borderRadius: 2,
                    p: 2,
                    mb: 2
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography sx={{ fontWeight: 600 }}>
                      {entry.target_name || entry.target_username || '-'}
                    </Typography>
                    <Chip label={statusLabel} color={statusColor} size="small" />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    Aktör: {entry.actor_name || entry.actor_username || '-'}
                    {entry.ip_address ? ` • IP: ${entry.ip_address}` : ''}
                  </Typography>
                  {entry.actor_type === 'authority' && (
                    <Typography variant="body2" color="text.secondary">
                      Hedef: {entry.target_username || '-'}
                    </Typography>
                  )}
                  <Typography
                    variant="body2"
                    sx={{ color: success ? 'success.main' : 'error.main', mt: 1, whiteSpace: 'pre-line' }}
                  >
                    {entry.message}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {entry.created_at ? new Date(entry.created_at).toLocaleString() : ''}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </CardContent>
      </Card>
    </Container>
  );
}

export default AuthorityPortal;

